<script lang="ts">

    import Modal from "@/components/Modal.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type {Field} from "@/types/types";
    import {useForm} from "@/lib/precognition.svelte";
    import {toast} from "svelte-sonner";
    import type {Document} from "@/types/types";
    import RelatedPolymorphic from "@/components/Inputs/RelatedPolymorphic.svelte";
    import EmployeeTable from "@/components/employees/EmployeeTable.svelte";
    import VehicleTable from "@/components/vehicles/VehicleTable.svelte";
    import FileUpload from "@/components/Inputs/FileUpload.svelte";
    import Checkbox from "@/components/Inputs/Checkbox.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        document : Document;
    };

    let {blocks, show = $bindable(), document }: Props = $props();

    let formInner: Record<string, any> = {};
    for (const block of blocks) {
        for (const field of block.fields) {
            formInner[field.column_name] = field.value;
        }
    }
    formInner['related_record'] = document.related_record_id;

    let form = $state(useForm(formInner, 'put', route('documents.update', { document: document.id })));

    function submit(e) {
        $form.processing = true;
        e.preventDefault();
        $form.put(route('documents.update', { document: document.id }), {
            onSuccess: () => {
                close();
                $form.reset();
                toast.success("Customer created successfully!");
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error("Error editing customer. " + message as string);
                }
            }
        });
    }



    function close() {
        show = false;
    }


    let isSelectRelatedOpen = $state(false);
    let relatedModule: string | null = $state(null);
    let relatedLabel: string | null = $state(null);

    let polymorphicFieldConfigs = {
        Vehicle: {
            component: VehicleTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    $form.related_record_id = row?.id ?? null;
                    $form.related_record_type = 'App\\Models\\Vehicle';
                    relatedLabel = row?.registration ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
        Employee: {
            component: EmployeeTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    $form.related_record_id = row?.id ?? null;
                    $form.related_record_type = 'App\\Models\\Employee';
                    relatedLabel = row?.name ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
    };
</script>

{#if show}
    <Modal title="Edit Document" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block}
                    <FormBlock label={block.label}>
                        {#each block.fields as field}
                            {#if field.editable}
                                {#if field.ui_type === 'polymorphic'}
                                    <RelatedPolymorphic
                                        label="Related Record"
                                        bind:module={relatedModule}
                                        bind:value={$form[field.column_name]}
                                        bind:valLabel={relatedLabel}
                                        error={$form.errors[field.column_name]}
                                        bind:isOpen={isSelectRelatedOpen}
                                        children={polymorphicFieldConfigs}
                                    />
                                {:else if field.ui_type === 'file'}
                                    <FileUpload
                                        allowMultiple={false}
                                        bind:value={$form['filename']}
                                        maxFileSize="30MB"
                                    />
                                {:else if field.ui_type === 'checkbox'}
                                    <Checkbox
                                        bind:checked={$form[field.column_name]}
                                        label={field.label}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else}
                                    <Input
                                        field={field}
                                        bind:value={$form[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$form.processing}>
                        {#if $form.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
